import { Button } from "../../../../components/ui/button";
import { Loader2 } from "lucide-react";
import axios from "axios";
import { BaseUrlApi, ErrorMessage } from "../../../../lib/api";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DatePicker } from "antd";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "src/components/ui/textarea";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { AlertDialogHeader } from "../../../../components/ui/alert-dialog";
import dayjs from "dayjs";

export default function TransactionDialog({
  open,
  onOpenChange,
  data,
  onSave,
  saving,
  errors,
}) {
  const [formData, setFormData] = useState({
    user_id: "",
    type: "",
    amount: "",
    reason: "",
    month: null,
  });

  useEffect(() => {
    if (data) {
      setFormData({
        user_id: data.user_id,
        type: data.type,
        amount: data.amount,
        reason: data.reason,
        month: data.month,
      });
    }
  }, [data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMonthChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      month: date ? date.format("YYYY-MM") : "",
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      user_id: formData.user_id,
      type: formData.type,
      amount: parseFloat(formData.amount),
      reason: formData.reason,
      month: formData.month,
    });
  };

  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchTeachers = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/users`);
      setTeachers(data.data.filter((user) => user?.role === "teacher"));
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object") toast("Error", { description: result });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeachers();
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-auto">
        <AlertDialogHeader>
          <DialogTitle>
            {data?.user_id ? "Edit Payment" : "Add Payment"}
          </DialogTitle>
        </AlertDialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {/* User ID */}
            <div className="grid gap-2">
              <label htmlFor="user_id">Select Teacher</label>
              <Select
                disabled={loading}
                value={+formData.user_id || ""}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, user_id: +value }))
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select Teacher" />
                </SelectTrigger>
                <SelectContent>
                  {teachers.length !== 0 ? (
                    teachers.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem>No Teachers Exist !</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {errors.user_id && (
                <p className="text-red-500 text-sm">{errors.user_id}</p>
              )}
            </div>

            {/* Type */}
            <div className="grid gap-2">
              <label htmlFor="type">Type</label>
              <Select
                value={formData.type}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, type: value }))
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="deduction">Deduction</SelectItem>
                  <SelectItem value="bonus">Bonus</SelectItem>
                </SelectContent>
              </Select>
              {errors.type && (
                <p className="text-red-500 text-sm">{errors.type}</p>
              )}
            </div>

            {/* Amount */}
            <div className="grid gap-2">
              <label htmlFor="amount">Amount</label>
              <Input
                type="number"
                id="amount"
                name="amount"
                step="0.01"
                value={formData.amount}
                onChange={handleChange}
                placeholder="e.g., 100.50"
                required
              />
              {errors.amount && (
                <p className="text-red-500 text-sm">{errors.amount}</p>
              )}
            </div>

            {/* Reason */}
            <div className="grid gap-2">
              <label htmlFor="reason">Reason</label>
              <Textarea
                id="reason"
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                placeholder="e.g., Payment for services"
                rows={3}
              />
              {errors.reason && (
                <p className="text-red-500 text-sm">{errors.reason}</p>
              )}
            </div>

            {/* Month */}
            <div className="grid gap-2">
              <label htmlFor="month">Month</label>
              <DatePicker
                picker="month"
                value={formData.month ? dayjs(formData.month) : null}
                onChange={handleMonthChange}
                className="w-full"
                format="YYYY-MM"
              />
              {errors.month && (
                <p className="text-red-500 text-sm">{errors.month}</p>
              )}
            </div>
          </div>

          <DialogFooter className="flex gap-2 justify-end">
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
